import {
  OrderTrackingIntegrationType,
  OrderTrackingOrderStatus,
  OrderTrackingShipmentStatus,
} from "@redotech/order-tracking-common/order-tracking-types";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery } from "@tanstack/react-query";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

const PLATFORM_OPTIONS: RedoListItem<string>[] = [
  { id: "redo", type: "text", text: "Redo", value: "redo" },
  { id: "malomo", type: "text", text: "Malomo", value: "malomo" },
];

const INTEGRATION_TYPE_LABELS: Record<OrderTrackingIntegrationType, string> = {
  [OrderTrackingIntegrationType.REDO]: "Redo",
  [OrderTrackingIntegrationType.KLAVIYO]: "Klaviyo",
  [OrderTrackingIntegrationType.ATTENTIVE]: "Attentive",
  [OrderTrackingIntegrationType.POSTSCRIPT]: "Postscript",
  [OrderTrackingIntegrationType.MOBILE_MESSAGE]: "Mobile Message",
};

const ORDER_STATUS_LABELS: Record<OrderTrackingOrderStatus, string> = {
  [OrderTrackingOrderStatus.CREATED]: "Order created",
  [OrderTrackingOrderStatus.FULFILLED]: "Order fulfilled",
  [OrderTrackingOrderStatus.STALLED_IN_FULFILLMENT]:
    "Order stalled in fulfillment",
  [OrderTrackingOrderStatus.AVAILABLE_FOR_PICKUP]:
    "Order available for pickup (store)",
  [OrderTrackingOrderStatus.CANCELLED]: "Order cancelled",
};

const SHIPMENT_STATUS_LABELS: Record<OrderTrackingShipmentStatus, string> = {
  [OrderTrackingShipmentStatus.UNKNOWN]: "Shipment unknown",
  [OrderTrackingShipmentStatus.PENDING]: "Shipment pending",
  [OrderTrackingShipmentStatus.PRE_TRANSIT]: "Shipment pre-transit",
  [OrderTrackingShipmentStatus.IN_TRANSIT]: "Shipment in transit",
  [OrderTrackingShipmentStatus.OUT_FOR_DELIVERY]: "Shipment out for delivery",
  [OrderTrackingShipmentStatus.DELIVERED]: "Shipment delivered",
  [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP]:
    "Shipment available for pickup",
  [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP_CARRIER]:
    "Shipment available for pickup (carrier)",
  [OrderTrackingShipmentStatus.STALLED_IN_TRANSIT]:
    "Shipment stalled in transit",
  [OrderTrackingShipmentStatus.RETURN_TO_SENDER]: "Shipment return to sender",
  [OrderTrackingShipmentStatus.DELIVERY_ATTEMPTED]:
    "Shipment delivery attempted",
  [OrderTrackingShipmentStatus.DELIVERY_FAILURE]: "Shipment delivery failure",
  [OrderTrackingShipmentStatus.SHIPMENT_CANCELLED]: "Shipment cancelled",
  [OrderTrackingShipmentStatus.SHIPMENT_ERROR]: "Shipment error",
};

const EVENT_TYPE_OPTIONS: RedoListItem<string>[] = [
  ...Object.values(OrderTrackingOrderStatus).map((value) => ({
    id: value,
    type: "text" as const,
    text: ORDER_STATUS_LABELS[value],
    value,
  })),
  ...Object.values(OrderTrackingShipmentStatus).map((value) => ({
    id: value,
    type: "text" as const,
    text: SHIPMENT_STATUS_LABELS[value],
    value,
  })),
];

export const IntegrationEventTestCard = memo(
  function IntegrationEventTestCard() {
    const team = useContext(TeamContext);
    const rpcClient = useContext(RedoAdminRpcClientContext);

    const [selectedPlatform, setSelectedPlatform] = useState<string>("");
    const [selectedIntegration, setSelectedIntegration] = useState<string>("");
    const [selectedEventType, setSelectedEventType] = useState<string>("");
    const [orderIdentifier, setOrderIdentifier] = useState<string>("");
    const [testEmail, setTestEmail] = useState<string>("");
    const [testPhoneNumber, setTestPhoneNumber] = useState<string>("");

    const { data: activeIntegrations } = useQuery({
      queryKey: ["activeOrderTrackingIntegrations", team?._id],
      queryFn: async () => {
        if (!team || !rpcClient) {
          return { integrations: [] };
        }
        return rpcClient.getActiveOrderTrackingIntegrations({
          teamId: team._id,
        });
      },
      enabled: Boolean(team && rpcClient),
    });

    const INTEGRATION_OPTIONS: RedoListItem<string>[] = useMemo(() => {
      if (!selectedPlatform || !activeIntegrations?.integrations) {
        return [];
      }

      return activeIntegrations.integrations
        .filter((integration) => integration.source === selectedPlatform)
        .map((integration) => ({
          id: integration.type,
          type: "text" as const,
          text: INTEGRATION_TYPE_LABELS[integration.type],
          value: integration.type,
        }));
    }, [activeIntegrations, selectedPlatform]);

    const sendTestMutation = useMutation({
      mutationFn: async () => {
        if (!team || !rpcClient) {
          throw new Error("Team or RPC client not available");
        }
        if (!selectedPlatform || !selectedIntegration || !selectedEventType) {
          throw new Error("All fields are required");
        }
        if (!testEmail && !testPhoneNumber) {
          throw new Error("At least one contact method is required");
        }

        return rpcClient.sendIntegrationEventTest({
          teamId: team._id,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          platform: selectedPlatform as "redo" | "malomo",
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          integration: selectedIntegration as OrderTrackingIntegrationType,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          eventType: selectedEventType as
            | OrderTrackingOrderStatus
            | OrderTrackingShipmentStatus,
          orderIdentifier: orderIdentifier.trim() || undefined,
          testEmail: testEmail || undefined,
          testPhoneNumber: testPhoneNumber || undefined,
        });
      },
      onSuccess: (response) => {
        if (response.success) {
          toast(response.message || "Test event sent successfully", {
            variant: "success",
          });
        } else {
          toast(response.error || "Failed to send test event", {
            variant: "error",
          });
        }
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        toast(`Failed to send test: ${errorMessage}`, { variant: "error" });
      },
    });

    const selectedPlatformItem = useMemo(
      () => PLATFORM_OPTIONS.find((opt) => opt.value === selectedPlatform),
      [selectedPlatform],
    );

    const selectedIntegrationItem = useMemo(
      () =>
        INTEGRATION_OPTIONS.find((opt) => opt.value === selectedIntegration),
      [selectedIntegration, INTEGRATION_OPTIONS],
    );

    const selectedEventTypeItem = useMemo(
      () => EVENT_TYPE_OPTIONS.find((opt) => opt.value === selectedEventType),
      [selectedEventType],
    );

    const handlePlatformSelected = useCallback(
      (args: { item: RedoListItem<string> }) => {
        setSelectedPlatform(args.item.value);
        // Reset integration selection when platform changes
        setSelectedIntegration("");
      },
      [],
    );

    const handleIntegrationSelected = useCallback(
      (args: { item: RedoListItem<string> }) => {
        setSelectedIntegration(args.item.value);
      },
      [],
    );

    const handleEventTypeSelected = useCallback(
      (args: { item: RedoListItem<string> }) => {
        setSelectedEventType(args.item.value);
      },
      [],
    );

    const canSendPayload =
      selectedPlatform &&
      selectedIntegration &&
      selectedEventType &&
      (testEmail || testPhoneNumber);

    if (!team || !rpcClient) {
      return null;
    }

    return (
      <Card title="Order Tracking V2 Health Check">
        <Flex dir="column" gap="lg">
          <Text fontSize="sm">
            Test integration events by sending payloads to email or SMS
            endpoints.
          </Text>

          <Flex dir="column" gap="md">
            <RedoSingleSelectDropdownInput
              label="Platform"
              options={PLATFORM_OPTIONS}
              optionSelected={handlePlatformSelected}
              placeholder="Select platform..."
              selectedItem={selectedPlatformItem}
            />

            <RedoSingleSelectDropdownInput
              label="Integration"
              options={INTEGRATION_OPTIONS}
              optionSelected={handleIntegrationSelected}
              placeholder="Select integration..."
              selectedItem={selectedIntegrationItem}
            />

            <RedoSingleSelectDropdownInput
              label="Event Type"
              options={EVENT_TYPE_OPTIONS}
              optionSelected={handleEventTypeSelected}
              placeholder="Select event type..."
              selectedItem={selectedEventTypeItem}
            />

            <RedoTextInput
              fullWidth
              label="Order (optional)"
              placeholder="Order name or number, e.g. #1001"
              setValue={setOrderIdentifier}
              value={orderIdentifier}
            />

            <RedoTextInput
              fullWidth
              label="Test Email"
              placeholder="test@example.com"
              setValue={setTestEmail}
              value={testEmail}
            />

            <RedoTextInput
              fullWidth
              label="Test Phone Number"
              placeholder="+1234567890"
              setValue={setTestPhoneNumber}
              value={testPhoneNumber}
            />

            <Flex justify="flex-start">
              <RedoButton
                disabled={!canSendPayload || sendTestMutation.isPending}
                hierarchy="primary"
                onClick={() => sendTestMutation.mutate()}
                size="md"
                text={
                  sendTestMutation.isPending ? "Sending..." : "Send Payload"
                }
              />
            </Flex>
          </Flex>
        </Flex>
      </Card>
    );
  },
);
